import { Button, Col, Collapse, Input, message, Modal, Row, Spin, Table, Typography } from "antd";
import Axios from "axios";
import React, { useEffect } from "react";
import { PlusOutlined, CheckCircleFilled, DeleteOutlined, SaveOutlined, GlobalOutlined, FontSizeOutlined, EnvironmentOutlined, RobotOutlined, DatabaseOutlined } from "@ant-design/icons";

const { Panel } = Collapse;

interface EnseignesWithSiretization {
    enseigne: string;
    category?: string;
    lines: SiretizationVerifData[];
}

interface SiretizationVerifData {
    siret: string;
    raison_sociale: string;
    raison_sociale_bot: string | null;
    address: string;
    postal_code: string;
    commune: string;
    addressBot: string | null;
    postalCodeBot: string | null;
    communeBot: string | null;
    siretScoreConfiance: string | null;
    siretScoreConfianceRaisonSociale: string | null;
    siretScoreConfianceAddress: string | null;
    siretOtherResults: SiretizationVerifData[] | null;
    url?: string;
    siretChoiceForAdress?: boolean;
    enseigne?: string;
    addedSiret?: boolean;
}

function buildSiretizationVerifData(siretOtherResult: SiretOtherResult, botValues: {
    raison_sociale_bot: string | null;
    addressBot: string | null;
    postalCodeBot: string | null;
    communeBot: string | null;
}): SiretizationVerifData {
    return {
        siret: siretOtherResult.siret,
        raison_sociale: siretOtherResult.raison_sociale,
        raison_sociale_bot: botValues.raison_sociale_bot,
        address: siretOtherResult.address,
        addressBot: botValues.addressBot,
        postalCodeBot: botValues.postalCodeBot,
        communeBot: botValues.communeBot,
        postal_code: "",
        commune: siretOtherResult.commune,
        siretScoreConfiance: siretOtherResult.score_confiance_global.toString(),
        siretScoreConfianceRaisonSociale: siretOtherResult.score_confiance_raison_sociale.toString(),
        siretScoreConfianceAddress: siretOtherResult.score_confiance_address.toString(),
        siretOtherResults: null,
        url: siretOtherResult.url_source,
    };
}

interface SiretOtherResult {
    siret: string;
    raison_sociale: string;
    raison_sociale_bot: string | null;
    code_ape: string;
    lib_ape: string;
    address: string;
    commune: string;
    addressBot: string | null;
    postalCodeBot: string | null;
    communeBot: string | null;
    url_source: string;
    score_confiance_raison_sociale: number;
    score_confiance_address: number;
    score_confiance_global: number;
}

function BotDbValue({ bot, db }: { bot: string | null | undefined, db: string | null | undefined }) {
    return (
        <div style={{ display: "flex", flexDirection: "column", gap: 2, lineHeight: 1.4 }}>
            <span><DatabaseOutlined style={{ marginRight: 6, color: "green" }} /><Typography.Text strong>{db ?? ""}</Typography.Text></span>
            <span><RobotOutlined style={{ marginRight: 6, color: "orange" }} />{bot ?? ""}</span>
        </div>
    );
}

function ExpandedBody({
    lines,
    enseigne,
    onAllTreatedChange,
    onRemoveAddedLine,
}: {
    lines: SiretizationVerifData[],
    enseigne: string,
    onAllTreatedChange: (allTreated: boolean) => void,
    onRemoveAddedLine: (siret: string) => void,
}) {
    const [modalData, setModalData] = React.useState<SiretizationVerifData | null>(null);
    const [validatedSirets, setValidatedSirets] = React.useState<{ oldSiret: string, validatedSirets: string }[]>([]);
    const [deletedSirets, setDeletedSirets] = React.useState<string[]>([]);
    const [modalValidatedSirets, setModalValidatedSirets] = React.useState<{ oldSiret: string, validatedSirets: string }[]>([]);

    useEffect(() => {
        const allTreated = lines.length > 0 && lines.every(line =>
            validatedSirets.some(v => v.oldSiret === line.siret) || deletedSirets.includes(line.siret)
        );
        onAllTreatedChange(allTreated);
    }, [lines, validatedSirets, deletedSirets]);

    const closeModal = () => {
        setModalData(null);
        setModalValidatedSirets([]);
    };

    return (
        <>
            <SiretTable
                lines={lines}
                enseigne={enseigne}
                setModalData={(data) => {
                    setModalValidatedSirets([]);
                    setModalData(data);
                }}
                validatedSirets={validatedSirets}
                setValidatedSirets={setValidatedSirets}
                deletedSirets={deletedSirets}
                setDeletedSirets={setDeletedSirets}
                onRemoveAddedLine={onRemoveAddedLine}
            />
            <Modal
                open={modalData != null}
                onCancel={closeModal}
                onOk={() => {
                    setValidatedSirets(prev => {
                        const withoutReplaced = prev.filter(
                            v => !modalValidatedSirets.some(m => m.oldSiret === v.oldSiret)
                        );
                        return [...withoutReplaced, ...modalValidatedSirets];
                    });
                    closeModal();
                }}
                //make fulscreen
                width={"90%"}
                height={"90%"}
                title={"Enseigne " + enseigne + " : choix du siret le plus approprié à l'adresse"}
                okText="Ok"
                cancelText="Annuler"
            >
                <div style={{ marginTop: "20px" }} >
                    <SiretTable
                        lines={modalData ? [modalData] : []}
                        enseigne={enseigne}
                        siretChoiceForAdress={true}
                        validatedSirets={modalValidatedSirets}
                        setValidatedSirets={setModalValidatedSirets}
                    />
                </div>
            </Modal>
        </>

    );
}


function SiretTable({
    lines,
    enseigne,
    setModalData,
    siretChoiceForAdress,
    validatedSirets,
    setValidatedSirets,
    deletedSirets = [],
    setDeletedSirets,
    onRemoveAddedLine,
}: {
    lines: SiretizationVerifData[],
    enseigne: string,
    setModalData?: (data: SiretizationVerifData | null) => void,
    siretChoiceForAdress?: boolean,
    validatedSirets: { oldSiret: string, validatedSirets: string }[],
    setValidatedSirets: React.Dispatch<React.SetStateAction<{ oldSiret: string, validatedSirets: string }[]>>,
    deletedSirets?: string[],
    setDeletedSirets?: React.Dispatch<React.SetStateAction<string[]>>,
    onRemoveAddedLine?: (siret: string) => void,
}) {
    const originalSiretForAdress = lines.length == 1 ? lines[0] : null;
    const selectedSiretForAdress =
        validatedSirets.find(v => v.oldSiret === originalSiretForAdress?.siret)?.validatedSirets
        ?? originalSiretForAdress?.siret
        ?? null;

    console.log(lines[0].siretOtherResults);
    console.log(siretChoiceForAdress);

    if (siretChoiceForAdress == true) {
        lines = [...lines.map(line => { return { ...line, siretChoiceForAdress: true } }), ...lines[0].siretOtherResults ?? []];
    }

    console.log(lines);

    return (
        <Table
            dataSource={lines}
            bordered={true}
            rowKey={(line, index) => `${line.siret}-${index}`}
            pagination={false}
            className="custom-table-row"
            rowClassName={(record: SiretizationVerifData) => record.siret == selectedSiretForAdress ? "row-selected" : ""}
        >
            <Table.Column
                title="Siret"
                dataIndex="siret"
                key="siret"
                width={170}
                render={(siret: string, record: SiretizationVerifData) => {
                    if (siretChoiceForAdress) {
                        return siret;
                    }
                    if (deletedSirets.includes(record.siret)) {
                        return (
                            <Typography.Text style={{ color: "red", fontWeight: 600 }}>
                                {siret}
                            </Typography.Text>
                        );
                    }
                    const validation = validatedSirets.find(v => v.oldSiret === record.siret);
                    if (validation && validation.validatedSirets !== validation.oldSiret) {
                        return (
                            <div>
                                <Typography.Text style={{ color: "orange" }} delete>
                                    {validation.oldSiret}
                                </Typography.Text>
                                <br />
                                <Typography.Text style={{ color: "green", fontWeight: 600 }}>
                                    {validation.validatedSirets}
                                </Typography.Text>
                            </div>
                        );
                    }
                    return <Typography.Text type={record.addedSiret ? "success" : "secondary"} style={{ fontWeight: 600 }}>{siret}</Typography.Text>;
                }}
            />
            <Table.Column
                title="Raison sociale"
                key="raison_sociale"
                render={(_: unknown, record: SiretizationVerifData) => (
                    <BotDbValue bot={record.raison_sociale_bot} db={record.raison_sociale} />
                )}
            />
            <Table.Column
                title="Adresse"
                key="adresse"
                render={(_: unknown, record: SiretizationVerifData) => (
                    <BotDbValue
                        bot={[record.addressBot, record.postalCodeBot, record.communeBot].filter(Boolean).join(" ")}
                        db={[record.address, record.postal_code, record.commune].filter(Boolean).join(" ")}
                    />
                )}
            />
            <Table.Column
                title="Scores"
                key="scores"
                width={90}
                render={(_: unknown, record: SiretizationVerifData) => (
                    <div style={{ display: "flex", flexDirection: "column", gap: 2, lineHeight: 1.4 }}>
                        <span><GlobalOutlined style={{ marginRight: 6 }} />{record.siretScoreConfiance?.split(".")[0] ?? ""}</span>
                        <span><FontSizeOutlined style={{ marginRight: 6 }} />{record.siretScoreConfianceRaisonSociale?.split(".")[0] ?? ""}</span>
                        <span><EnvironmentOutlined style={{ marginRight: 6 }} />{record.siretScoreConfianceAddress?.split(".")[0] ?? ""}</span>
                    </div>
                )}
            />
            <Table.Column
                title={
                    siretChoiceForAdress ? (
                        "Validation"
                    ) : (

                        <Button
                            type="primary"
                            icon={<CheckCircleFilled />}
                            disabled={lines.every(line =>
                                validatedSirets.some(v => v.oldSiret === line.siret)
                                || deletedSirets.includes(line.siret)
                            )}
                            onClick={() => {
                                setValidatedSirets(prev => {
                                    const untreated = lines.filter(line =>
                                        !prev.some(v => v.oldSiret === line.siret)
                                        && !deletedSirets.includes(line.siret)
                                    );
                                    return [
                                        ...prev,
                                        ...untreated.map(line => ({
                                            oldSiret: line.siret,
                                            validatedSirets: line.siret,
                                        })),
                                    ];
                                });
                            }}
                        >
                            Valider tout
                        </Button>
                    )
                }
                dataIndex="siretOtherResults"
                key="siretOtherResults"
                render={(_, record: SiretizationVerifData) => {

                    const isValidated = validatedSirets.some(v => v.oldSiret === record.siret);
                    const isDeleted = deletedSirets.includes(record.siret);
                    return (
                        <>
                            {siretChoiceForAdress ? (
                                <Button
                                    disabled={record.siret == selectedSiretForAdress}
                                    icon={<CheckCircleFilled style={{ color: record.siret == selectedSiretForAdress ? "green" : undefined }} />}
                                    type="primary"
                                    onClick={() => {
                                        setValidatedSirets(prev => [
                                            ...prev.filter(v => v.oldSiret !== originalSiretForAdress?.siret),
                                            { oldSiret: originalSiretForAdress?.siret || "", validatedSirets: record.siret },
                                        ]);
                                    }}
                                >
                                    Choisir
                                </Button>
                            ) : (
                                <Col span={24}>
                                    <Row>
                                        <Button color={isValidated ? "green" : isDeleted ? "red" : undefined} variant={isValidated ? "outlined" : isDeleted ? "outlined" : undefined} icon={<CheckCircleFilled />} type="primary" onClick={isValidated || isDeleted ? undefined : () => {
                                            setValidatedSirets([...validatedSirets, { oldSiret: record.siret, validatedSirets: record.siret }]);
                                        }}>
                                            {isValidated ? "Validé à l'instant" : isDeleted ? "Supprimé à l'instant" : "Valider"}
                                        </Button>
                                        {!isValidated && !isDeleted && (
                                            <>
                                                <Button style={{ marginLeft: "10px" }}
                                                    icon={<DeleteOutlined />} danger onClick={() => {
                                                        if (record.addedSiret) {
                                                            onRemoveAddedLine?.(record.siret);
                                                        } else {
                                                            setDeletedSirets?.(prev => prev.includes(record.siret) ? prev : [...prev, record.siret]);
                                                        }
                                                    }}>
                                                    Supprimer
                                                </Button>
                                            </>

                                        )}
                                    </Row>
                                    <br />
                                    {isValidated || isDeleted ? (
                                        <Button onClick={() => {
                                            if (isDeleted) {
                                                setDeletedSirets?.(prev => prev.filter(s => s !== record.siret));
                                            } else {
                                                setValidatedSirets(prev => prev.filter(v => v.oldSiret !== record.siret));
                                            }
                                        }}>
                                            Annuler
                                        </Button>
                                    ) : ((record.siretOtherResults?.length || 0) > 0) && setModalData && (
                                        <Button color="orange" variant="solid" onClick={() => {
                                            setModalData(record);
                                        }}>
                                            {"Autres suggestions (" + record.siretOtherResults?.length + ")"} ?
                                        </Button>)}
                                </Col>
                            )}
                        </>

                    );
                }}
            />
        </Table>
    );
}

export function VerifyDataSirets({ manualEnseigne }: { manualEnseigne?: string }) {
    const [dataToVerify, setDataToVerify] = React.useState<EnseignesWithSiretization[] | null>(null);
    const [allTreatedByEnseigne, setAllTreatedByEnseigne] = React.useState<Record<string, boolean>>({});
    const [loading, setLoading] = React.useState(false);
    const oldManualEnseigne = React.useRef<string | null>(null);

    useEffect(() => {
        Axios.get("/ms/bots/getSiretsToVerify")
            .then((response) => {
                setDataToVerify(response.data.map((e: any) => {
                    return {
                        ...e, lines: e.lines.map((l: any) => {
                            if (l.siretOtherResults) {
                                return { ...l, siretOtherResults: (JSON.parse(l.siretOtherResults) as SiretOtherResult[]).map(e => buildSiretizationVerifData(e, { raison_sociale_bot: l.raison_sociale_bot, addressBot: l.addressBot, postalCodeBot: l.postalCodeBot, communeBot: l.communeBot })) };
                            } else {
                                return { ...l, siretOtherResults: null };
                            }
                        })
                    };
                }));
            })
            .catch((error) => {
                console.error("Erreur lors du chargement des sirets", error);
            });
    }, []);

    useEffect(() => {
        if (manualEnseigne && manualEnseigne != oldManualEnseigne.current && dataToVerify != null) {
            oldManualEnseigne.current = manualEnseigne;
            setLoading(true);
            Axios.get("/ms/manualSiretizationLinesToVerify/" + encodeURIComponent(manualEnseigne))
                .then((response) => {
                    const lines = response.data as SiretizationVerifData[];
                    setDataToVerify([
                        { enseigne: manualEnseigne, lines },
                        ...(dataToVerify ?? []).filter(e => e.enseigne !== manualEnseigne),
                    ]);
                    setLoading(false);
                    setTimeout(() => {
                        const panelKey = `${manualEnseigne}-0`;
                        document.getElementById(panelKey)?.scrollIntoView({ behavior: "smooth" });
                        document.getElementById(panelKey)?.click();
                    }, 200);
                }).catch((error) => {
                    console.error("Erreur lors du chargement des sirets", error);
                    message.error("Erreur lors du chargement des lignes de l'enseigne " + manualEnseigne);
                    setLoading(false);
                    oldManualEnseigne.current = null;
                })
        }
    }, [manualEnseigne, dataToVerify]);

    return (
        <div style={dataToVerify == null || dataToVerify.length === 0 ? { textAlign: "center", marginTop: "50px" } : {}}>
            {dataToVerify == null || loading ? (
                <Spin size="large" tip="Chargement" />
            ) : dataToVerify.length === 0 ? (
                <Typography.Text type="secondary">✅ Aucun siret à valider</Typography.Text>
            ) : (
                <Collapse>
                    {dataToVerify.map((enseigneData, index) => {
                        const panelKey = `${enseigneData.enseigne}-${index}`;
                        return (
                            <Panel

                                style={{ marginTop: "10px" }}
                                header={
                                    <div id={panelKey} style={{ display: "flex", alignItems: "center", gap: "20px" }}>
                                        <span>{enseigneData.enseigne}</span>
                                        <Typography.Text type="secondary">
                                            {enseigneData.lines.length} ligne{enseigneData.lines.length > 1 ? "s" : ""}
                                        </Typography.Text>
                                    </div>
                                }
                                extra={
                                    <Button
                                        icon={<SaveOutlined />}
                                        type="primary"
                                        disabled={!allTreatedByEnseigne[panelKey]}
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        Sauvegarder
                                    </Button>
                                }
                                key={panelKey}
                            >
                                <AddSiretButton enseigne={enseigneData.enseigne} dataToVerify={dataToVerify} setDataToVerify={setDataToVerify} />
                                {enseigneData.lines.length === 0 ? (
                                    <Typography.Text type="secondary">Aucune donnée à valider</Typography.Text>
                                ) : (
                                    <ExpandedBody
                                        lines={enseigneData.lines}
                                        enseigne={enseigneData.enseigne}
                                        onAllTreatedChange={(allTreated) => {
                                            setAllTreatedByEnseigne(prev =>
                                                prev[panelKey] === allTreated ? prev : { ...prev, [panelKey]: allTreated }
                                            );
                                        }}
                                        onRemoveAddedLine={(siret) => {
                                            setDataToVerify(prev =>
                                                (prev ?? []).map(e =>
                                                    e.enseigne === enseigneData.enseigne
                                                        ? { ...e, lines: e.lines.filter(l => !(l.addedSiret && l.siret === siret)) }
                                                        : e
                                                )
                                            );
                                        }}
                                    />
                                )}
                            </Panel>
                        );
                    })}
                </Collapse>
            )}
        </div>
    );
}


function AddSiretButton({ enseigne, dataToVerify, setDataToVerify }: { enseigne: string, dataToVerify: EnseignesWithSiretization[] | null, setDataToVerify: React.Dispatch<React.SetStateAction<EnseignesWithSiretization[] | null>> }) {

    const [addingSiret, setAddingSiret] = React.useState(false);
    const [loading, setLoading] = React.useState(false);

    const [siret, setSiret] = React.useState("");
    return (
        <>
            <>
                {
                    addingSiret ? (
                        <>
                            <Row style={{ marginBottom: "10px" }} gutter={10}>
                                <Col span={12}>
                                    <Input type="text" placeholder="Ajouter un siret" value={siret} onChange={e => setSiret(e.target.value)} />
                                </Col>
                                <Col span={12}>
                                    <Button type="primary" loading={loading} icon={<PlusOutlined />} disabled={siret.length !== 14 || dataToVerify?.some(e => e.enseigne == enseigne && e.lines.some(l => l.siret == siret))} onClick={() => {
                                        setLoading(true);
                                        Axios.get("/ms/getSiretizationData/" + siret)
                                            .then((response) => {
                                                setLoading(false);
                                                setDataToVerify((dataToVerify ?? []).map(e => {
                                                    if (e.enseigne == enseigne) {
                                                        return {
                                                            ...e,
                                                            lines: [{ ...response.data, addedSiret: true }].concat(e.lines)
                                                        }
                                                    } else {
                                                        return e;
                                                    }
                                                }));
                                            }).catch((error: any) => {
                                                console.log("Erreur lors de l'ajout du siret", error);
                                                message.error("Erreur: " + error.toString().includes("status code 404") ? "Siret non trouvé" : "Erreur lors de l'ajout du siret");
                                                setLoading(false);
                                            })
                                    }
                                    }>
                                        Ajouter
                                    </Button>
                                    {
                                        dataToVerify?.some(e => e.enseigne == enseigne && e.lines.some(l => l.siret == siret)) && (
                                            <Typography.Text style={{ color: "red", marginLeft: "10px" }} type="secondary">⚠️ Siret déjà présent dans l'enseigne</Typography.Text>
                                        )
                                    }
                                </Col>
                            </Row>
                        </>
                    ) : (
                        <Button style={{ marginBottom: "10px" }} type="primary" icon={<PlusOutlined />} onClick={() => {
                            setAddingSiret(true);
                        }}>
                            Ajouter un siret
                        </Button>)
                }
            </>
        </>


    );
}